/**
 * Tenant-defined conversation statuses (`conversation_status`), shared by every
 * surface that renders a status — detail strip, header pill, inbox table.
 *
 * The catalog is small, changes rarely and is read by several components per
 * screen, so the fetch is memoised at module scope and de-duplicated while
 * in flight; only an explicit `refetch()` goes back to the API.
 */
import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'
import type { ConversationStatusOption } from '../modules/conversations/statusCatalog.js'

interface DropdownRow {
  id: string
  name: string
  statusColor?: string | null
  statusColorType?: string | null
}

let cached: ConversationStatusOption[] | null = null
let inflight: Promise<ConversationStatusOption[]> | null = null

async function loadStatuses(force = false): Promise<ConversationStatusOption[]> {
  if (!force && cached) return cached
  if (!force && inflight) return inflight
  const request = api
    .get<{ status: DropdownRow[] }>('/admin/conversation-management/status/dropdown')
    .then((res) => {
      const rows = (res.status ?? []).map((r) => ({
        id: r.id,
        name: r.name,
        color: r.statusColor ?? null,
        colorType: r.statusColorType ?? null,
      }))
      cached = rows
      return rows
    })
    .catch(() => cached ?? [])
    .finally(() => {
      inflight = null
    })
  inflight = request
  return request
}

/** Drop the memoised catalog — used by tests and after an admin edit. */
export function resetConversationStatusCache(): void {
  cached = null
  inflight = null
}

export interface UseConversationStatusesResult {
  statuses: ConversationStatusOption[]
  loading: boolean
  refetch: () => Promise<void>
}

export function useConversationStatuses(): UseConversationStatusesResult {
  const [statuses, setStatuses] = useState<ConversationStatusOption[]>(() => cached ?? [])
  const [loading, setLoading] = useState(cached === null)

  useEffect(() => {
    let alive = true
    if (cached) {
      setStatuses(cached)
      setLoading(false)
      return
    }
    void loadStatuses().then((rows) => {
      if (!alive) return
      setStatuses(rows)
      setLoading(false)
    })
    return () => {
      alive = false
    }
  }, [])

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true)
    const rows = await loadStatuses(true)
    setStatuses(rows)
    setLoading(false)
  }, [])

  return { statuses, loading, refetch }
}
